import { useState, useEffect } from "react";
import "./ClientTable.css";

export default function ClientTable({ clients, onEdit, onDelete }) {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 20;

  // =========================
  // RECHERCHE
  // =========================
  const filteredClients = (clients || []).filter((client) => {
    const q = (search || "").trim().toLowerCase();

    if (!q) return true;

    return (
      String(client?.nom || "")
        .toLowerCase()
        .includes(q) ||

      String(client?.email || "")
        .toLowerCase()
        .includes(q) ||

      String(client?.telephone || "")
        .toLowerCase()
        .includes(q) ||

      String(client?.adresse || "")
        .toLowerCase()
        .includes(q)
    );
  });

  // =========================
  // PAGINATION
  // =========================
  const totalPages = Math.ceil(
    filteredClients.length / itemsPerPage
  );

  // Revenir à la page 1 lorsqu'on recherche
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // Si suppression d'un client et que la page actuelle
  // n'existe plus, revenir à la dernière page disponible
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }

    if (totalPages === 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const startIndex =
    (currentPage - 1) * itemsPerPage;

  const endIndex =
    startIndex + itemsPerPage;

  const paginatedClients =
    filteredClients.slice(startIndex, endIndex);

  // =========================
  // NAVIGATION
  // =========================
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const clearSearch = () => {
    setSearch("");
    setCurrentPage(1);
  };

  return (
    <div className="table-container">

      {/* =========================
          BARRE DE RECHERCHE
      ========================= */}
      <div className="client-table-toolbar">

        <div className="client-table-search">
          <span className="search-icon">🔍</span>

          <input
            type="text"
            placeholder="Rechercher un client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {search && (
            <button
              type="button"
              className="clear-search"
              onClick={clearSearch}
              title="Effacer la recherche"
            >
              ✕
            </button>
          )}
        </div>

        <div className="client-table-count">
          {filteredClients.length} client
          {filteredClients.length !== 1 ? "s" : ""}
        </div>

      </div>

      {/* =========================
          TABLEAU
      ========================= */}
      {filteredClients.length === 0 ? (

        <div className="no-clients-found">

          <div className="no-clients-icon">
            👤
          </div>

          <h3>
            {search
              ? "Aucun client trouvé"
              : "Aucun client"}
          </h3>

          <p>
            {search
              ? `Aucun client ne correspond à « ${search} ».`
              : "Aucun client n'est enregistré."}
          </p>

          {search && (
            <button
              type="button"
              className="btn-clear-client-search"
              onClick={clearSearch}
            >
              Effacer la recherche
            </button>
          )}

        </div>

      ) : (

        <div className="responsive-table-wrapper">

          <table className="responsive-table">

            <thead>
              <tr>
                <th>Client</th>
                <th>Téléphone</th>
                <th>Adresse</th>
                <th>Email</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>

              {paginatedClients.map((client) => (

                <tr key={client.id}>

                  {/* CLIENT */}
                  <td data-label="Client">
                    <strong>
                      {client.nom || "-"}
                    </strong>
                  </td>

                  {/* TELEPHONE */}
                  <td data-label="Téléphone">

                    {client.telephone ? (
                      <a
                        href={`tel:${client.telephone}`}
                        className="phone-link"
                      >
                        📞 {client.telephone}
                      </a>
                    ) : (
                      <span className="empty-value">
                        -
                      </span>
                    )}

                  </td>

                  {/* ADRESSE */}
                  <td data-label="Adresse">
                    {client.adresse || (
                      <span className="empty-value">
                        -
                      </span>
                    )}
                  </td>

                  {/* EMAIL */}
                  <td data-label="Email">

                    {client.email ? (
                      <a
                        href={`mailto:${client.email}`}
                        className="email-link"
                      >
                        {client.email}
                      </a>
                    ) : (
                      <span className="empty-value">
                        -
                      </span>
                    )}

                  </td>

                  {/* ACTIONS */}
                  <td data-label="Actions">

                    <div className="client-actions">

                      <button
                        type="button"
                        className="btn edit"
                        onClick={() => onEdit(client)}
                        title="Modifier le client"
                      >
                        ✏️
                      </button>

                      <button
                        type="button"
                        className="btn delete"
                        onClick={() => onDelete(client.id)}
                        title="Supprimer le client"
                      >
                        🗑️
                      </button>

                    </div>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      )}

      {/* =========================
          INFORMATIONS PAGINATION
      ========================= */}
      {filteredClients.length > 0 && (
        <div className="client-pagination-container">

          <div className="pagination-info">

            Affichage de{" "}
            <strong>
              {startIndex + 1}
            </strong>{" "}
            à{" "}
            <strong>
              {Math.min(
                endIndex,
                filteredClients.length
              )}
            </strong>{" "}
            sur{" "}
            <strong>
              {filteredClients.length}
            </strong>{" "}
            client
            {filteredClients.length !== 1 ? "s" : ""}

          </div>

          {/* =========================
              PAGINATION
          ========================= */}
          {totalPages > 1 && (

            <div className="pagination">

              <button
                type="button"
                disabled={currentPage === 1}
                onClick={goToPreviousPage}
                title="Page précédente"
              >
                ◀
              </button>

              {Array.from(
                { length: totalPages },
                (_, index) => index + 1
              ).map((page) => (

                <button
                  type="button"
                  key={page}
                  className={
                    currentPage === page
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    setCurrentPage(page)
                  }
                >
                  {page}
                </button>

              ))}

              <button
                type="button"
                disabled={
                  currentPage === totalPages
                }
                onClick={goToNextPage}
                title="Page suivante"
              >
                ▶
              </button>

            </div>

          )}

        </div>
      )}

    </div>
  );
}
